import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { hiveService, apiaryService } from '../services/firebase';
import { Hive, Apiary } from '../types';
import EnhancedInspectionForm from './EnhancedInspectionForm';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import './Dashboard.css';
import './HivesPageEnhanced.css';

// Add QRCode type declaration
declare global {
  interface Window {
    QRCode: any;
  }
}

const HivesPageEnhanced: React.FC = () => {
  const { user } = useAuth();
  const [hives, setHives] = useState<Hive[]>([]);
  const [filteredHives, setFilteredHives] = useState<Hive[]>([]);
  const [apiaries, setApiaries] = useState<Apiary[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHive, setEditingHive] = useState<Hive | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [inspectionHive, setInspectionHive] = useState<Hive | null>(null);
  const [inspectionApiary, setInspectionApiary] = useState<Apiary | null>(null);
  
  // Filter states
  const [selectedApiary, setSelectedApiary] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // QR Modal states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrHive, setQrHive] = useState<Hive | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    apiaryId: '',
    queen_origin: '',
    queen_marked: false,
    queen_marking_color: '' as 'white' | 'yellow' | 'red' | 'green' | 'blue' | '',
    established: new Date().toISOString().split('T')[0],
    status: 'active' as 'active' | 'dormant' | 'needs_inspection'
  });

  const resetFormData = () => {
    setFormData({
      name: '',
      apiaryId: '',
      queen_origin: '',
      queen_marked: false,
      queen_marking_color: '' as 'white' | 'yellow' | 'red' | 'green' | 'blue' | '',
      established: new Date().toISOString().split('T')[0],
      status: 'active' as 'active' | 'dormant' | 'needs_inspection'
    });
  };

  // Load QRCode library
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      console.log('Loading hives and apiaries data...');
      const [hivesData, apiariesData] = await Promise.all([
        hiveService.getUserHives(user.uid),
        apiaryService.getUserApiaries(user.uid),
      ]);
      console.log('Loaded hives:', hivesData);
      console.log('Loaded apiaries:', apiariesData);
      setHives(hivesData);
      setApiaries(apiariesData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  // Filter hives when filters change
  useEffect(() => {
    let filtered = [...hives];

    // Filter by apiary
    if (selectedApiary) {
      filtered = filtered.filter(hive => hive.apiary_id === selectedApiary);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(hive => getHiveStatus(hive) === selectedStatus);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(hive => 
        hive.name.toLowerCase().includes(term) ||
        hive.queen_origin?.toLowerCase().includes(term) ||
        apiaries.find(a => a.id === hive.apiary_id)?.name.toLowerCase().includes(term)
      );
    }

    setFilteredHives(filtered);
  }, [hives, selectedApiary, selectedStatus, searchTerm, apiaries]);

  const getHiveStatus = (hive: Hive): 'active' | 'dormant' | 'needs_inspection' => {
    // This is a placeholder - you might want to implement actual status logic
    // based on inspection dates, user-defined status, etc.
    if (hive.status) return hive.status;
    
    // Example logic: if no inspection in 14 days, needs inspection
    const lastInspection = hive.last_inspection;
    if (lastInspection) {
      const daysSinceInspection = Math.floor((Date.now() - new Date(lastInspection).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceInspection > 14) return 'needs_inspection';
    }
    
    return 'active';
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'dormant':
        return 'Dormant';
      case 'needs_inspection':
        return 'Needs Check';
      default:
        return 'Active';
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'active':
        return '';
      case 'dormant':
        return 'warning';
      case 'needs_inspection':
        return 'warning';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.name.trim() || !formData.apiaryId) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      if (editingHive) {
        const updates: Partial<Hive> = {
          name: formData.name,
          queen_origin: formData.queen_origin || undefined,
          queen_marked: formData.queen_marked,
          queen_marking_color: formData.queen_marked && formData.queen_marking_color ? formData.queen_marking_color : undefined,
          established: formData.established,
          apiary_id: formData.apiaryId,
          status: formData.status
        };
        
        await hiveService.updateHive(editingHive.id, updates);
        console.log('Hive updated successfully');
        alert('Hive updated successfully!');
      } else {
        const newHiveData: Omit<Hive, 'id'> = {
          name: formData.name,
          queen_origin: formData.queen_origin || undefined,
          queen_marked: formData.queen_marked,
          queen_marking_color: formData.queen_marked && formData.queen_marking_color ? formData.queen_marking_color : undefined,
          established: formData.established,
          apiary_id: formData.apiaryId,
          status: formData.status
        };
        
        const hiveId = await hiveService.addHive(newHiveData);
        console.log('Hive saved successfully with ID:', hiveId);
        alert('Hive saved successfully!');
      }
      
      resetFormData();
      setShowAddForm(false);
      setEditingHive(null);
      loadData();
    } catch (error) {
      console.error('Error saving hive:', error);
      alert(`Error saving hive: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEditHive = (hive: Hive) => {
    setFormData({
      name: hive.name,
      apiaryId: hive.apiary_id,
      queen_origin: hive.queen_origin || '',
      queen_marked: hive.queen_marked || false,
      queen_marking_color: hive.queen_marking_color || '',
      established: hive.established,
      status: hive.status || 'active'
    });
    setEditingHive(hive);
    setShowAddForm(true);
  };

  const handleViewHiveInspections = (hive: Hive) => {
    const apiary = apiaries.find(a => a.id === hive.apiary_id);
    setInspectionHive(hive);
    setInspectionApiary(apiary || null);
    setShowInspectionForm(true);
  };

  const handleCloseInspectionForm = () => {
    setShowInspectionForm(false);
    setInspectionHive(null);
    setInspectionApiary(null);
  };

  const handleInspectionSaved = () => {
    console.log('Inspection saved successfully');
    loadData(); // Reload to update last inspection dates
  };

  const handleDeleteHive = async (hive: Hive) => {
    if (!window.confirm(`Are you sure you want to delete "${hive.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await hiveService.deleteHive(hive.id);
      alert('Hive deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting hive:', error);
      alert(`Error deleting hive: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const showQRCode = (hive: Hive) => {
    setQrHive(hive);
    setShowQRModal(true);
    
    // Generate QR code after modal is shown
    setTimeout(() => {
      const qrcodeDiv = document.getElementById('qrcode');
      if (qrcodeDiv && window.QRCode) {
        qrcodeDiv.innerHTML = '';
        const hiveUrl = `${window.location.origin}/hive/${hive.id}`;
        
        new window.QRCode(qrcodeDiv, {
          text: hiveUrl,
          width: 200,
          height: 200,
          colorDark: "#2C3E50",
          colorLight: "#ffffff",
          correctLevel: window.QRCode.CorrectLevel?.H || 0
        });
        
        const urlDisplay = document.getElementById('qrUrl');
        if (urlDisplay) {
          urlDisplay.textContent = hiveUrl;
        }
      }
    }, 100);
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrHive(null);
  };

  const printQRCode = () => {
    window.print();
  };

  const handleImageUpload = async (hive: Hive, file: File) => {
    if (!user) return;
    
    try {
      console.log('Uploading image for hive:', hive.name, file);
      setUploadingImage(hive.id);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `hive_${hive.id}_${timestamp}.${fileExtension}`;
      
      // Create storage reference
      const storageRef = ref(storage, `hive-images/${user.uid}/${fileName}`);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      console.log('Image uploaded successfully:', snapshot);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Download URL:', downloadURL);
      
      // Update the hive with the image URL
      await hiveService.updateHive(hive.id, { imageUrl: downloadURL });
      
      // Reload data to show the new image
      await loadData();
      
      console.log('Hive image updated successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingImage(null);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-text">Loading hives...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Enhanced Page Header */}
      <div className="hives-page-header">
        <div className="hives-page-title">
          <h2>🏠 My Hives</h2>
          <span className="hive-count">{filteredHives.length} Hives</span>
        </div>
        <button 
          className="add-hive-btn" 
          onClick={() => {
            resetFormData();
            setEditingHive(null);
            setShowAddForm(true);
          }}
          disabled={apiaries.length === 0}
          title={apiaries.length === 0 ? "Add an apiary first before creating hives" : "Add a new hive to your apiaries"}
        >
          <span>+</span>
          <span>Add New Hive</span>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="filter-label">Apiary:</label>
          <select 
            className="filter-select" 
            value={selectedApiary} 
            onChange={(e) => setSelectedApiary(e.target.value)}
          >
            <option value="">All Apiaries</option>
            {apiaries.map(apiary => (
              <option key={apiary.id} value={apiary.id}>{apiary.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Status:</label>
          <select 
            className="filter-select" 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="dormant">Dormant</option>
            <option value="needs_inspection">Needs Inspection</option>
          </select>
        </div>
        <div className="search-box">
          <span>🔍</span>
          <input 
            type="text" 
            placeholder="Search hives..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="page-content">
        {apiaries.length === 0 ? (
          <div className="empty-state">
            <h3>🏡 No Apiaries Found</h3>
            <p>You need to add an apiary first before you can add hives.</p>
            <p>Go to the <strong>Apiaries</strong> section to create your first apiary location.</p>
          </div>
        ) : filteredHives.length === 0 && hives.length === 0 ? (
          <div className="empty-state">
            <h3>🏠 No Hives Yet</h3>
            <p>Start by adding your first hive to begin tracking your beekeeping journey!</p>
            <button 
              className="add-hive-btn" 
              onClick={() => {
                resetFormData();
                setEditingHive(null);
                setShowAddForm(true);
              }}
            >
              Add Your First Hive
            </button>
          </div>
        ) : filteredHives.length === 0 ? (
          <div className="empty-state">
            <h3>🔍 No Hives Match Your Filters</h3>
            <p>Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="hives-grid">
            {filteredHives.map(hive => {
              const apiary = apiaries.find(a => a.id === hive.apiary_id);
              const status = getHiveStatus(hive);
              
              return (
                <div key={hive.id} className="hive-card">
                  <div className="hive-image-container">
                    {uploadingImage === hive.id ? (
                      <div className="hive-image-placeholder">
                        <span className="upload-icon">⏳</span>
                        <span className="upload-text">Uploading...</span>
                      </div>
                    ) : hive.imageUrl ? (
                      <img 
                        src={hive.imageUrl} 
                        alt={`${hive.name} hive`} 
                        className="hive-image"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleImageUpload(hive, file);
                            }
                          };
                          input.click();
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to change photo"
                      />
                    ) : (
                      <div 
                        className="hive-image-placeholder"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleImageUpload(hive, file);
                            }
                          };
                          input.click();
                        }}
                      >
                        <span className="upload-icon">📷</span>
                        <span className="upload-text">Click to add photo</span>
                      </div>
                    )}
                    <span className={`hive-status ${getStatusClass(status)}`}>
                      {getStatusLabel(status)}
                    </span>
                  </div>
                  <div className="hive-details">
                    <h3 className="hive-name">{hive.name}</h3>
                    <div className="hive-info">
                      <div className="info-row">
                        <span className="info-label">Apiary:</span>
                        <span>{apiary?.name || 'Unknown'}</span>
                      </div>
                      {hive.queen_origin && (
                        <div className="info-row">
                          <span className="info-label">Queen Origin:</span>
                          <span>{hive.queen_origin}</span>
                        </div>
                      )}
                      <div className="info-row">
                        <span className="info-label">Established:</span>
                        <span>{new Date(hive.established).toLocaleDateString()}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Last Inspection:</span>
                        <span>{hive.last_inspection ? `${new Date(hive.last_inspection).toLocaleDateString()} (${Math.floor((Date.now() - new Date(hive.last_inspection).getTime()) / (1000 * 60 * 60 * 24))} days ago)` : 'Never'}</span>
                      </div>
                    </div>
                    <div className="hive-actions">
                      <button 
                        className="action-btn"
                        onClick={() => handleEditHive(hive)}
                        title="Edit hive details"
                      >
                        <span>✏️</span>
                        <span>Edit</span>
                      </button>
                      <button 
                        className="action-btn primary"
                        onClick={() => handleViewHiveInspections(hive)}
                        title="Start new inspection for this hive"
                      >
                        <span>🔍</span>
                        <span>Inspect</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => showQRCode(hive)}
                        title="Generate QR code for this hive"
                      >
                        <span>📱</span>
                        <span>QR Code</span>
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => handleDeleteHive(hive)}
                        title="Delete this hive"
                      >
                        <span>🗑️</span>
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className={`qr-modal ${showQRModal ? 'active' : ''}`} onClick={(e) => {
          if (e.target === e.currentTarget) closeQRModal();
        }}>
          <div className="qr-modal-content">
            <div className="qr-modal-header">
              <h3 className="qr-modal-title">{qrHive?.name} - QR Code</h3>
              <p className="qr-modal-subtitle">Scan this code to access hive information</p>
            </div>
            <div className="qr-container">
              <div className="qr-code" id="qrcode"></div>
              <div className="qr-url" id="qrUrl"></div>
            </div>
            <div className="qr-modal-actions">
              <button className="modal-btn print" onClick={printQRCode}>
                🖨️ Print QR Code
              </button>
              <button className="modal-btn close" onClick={closeQRModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Hive Modal (keep existing modal) */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingHive ? 'Edit Hive' : 'Add New Hive'}</h3>
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowAddForm(false);
                  setEditingHive(null);
                  resetFormData();
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="add-form">
              <div className="form-group">
                <label>Hive Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter hive name"
                />
              </div>

              <div className="form-group">
                <label>Apiary *</label>
                <select
                  name="apiaryId"
                  value={formData.apiaryId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select an apiary</option>
                  {apiaries.map(apiary => (
                    <option key={apiary.id} value={apiary.id}>
                      {apiary.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Queen Origin</label>
                <input
                  type="text"
                  name="queen_origin"
                  value={formData.queen_origin}
                  onChange={handleInputChange}
                  placeholder="e.g., Local swarm, Purchased from..."
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="dormant">Dormant</option>
                  <option value="needs_inspection">Needs Inspection</option>
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="queen_marked"
                    checked={formData.queen_marked}
                    onChange={handleInputChange}
                  />
                  Queen is marked
                </label>
              </div>

              {formData.queen_marked && (
                <div className="form-group">
                  <label>Queen Marking Color</label>
                  <select
                    name="queen_marking_color"
                    value={formData.queen_marking_color}
                    onChange={handleInputChange}
                  >
                    <option value="">Select color</option>
                    <option value="white">⚪ White</option>
                    <option value="yellow">🟡 Yellow</option>
                    <option value="red">🔴 Red</option>
                    <option value="green">🟢 Green</option>
                    <option value="blue">🔵 Blue</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Established Date</label>
                <input
                  type="date"
                  name="established"
                  value={formData.established}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  {editingHive ? 'Update Hive' : 'Add Hive'}
                </button>
                <button 
                  type="button" 
                  className="secondary-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingHive(null);
                    resetFormData();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inspection Form Modal */}
      {showInspectionForm && inspectionHive && inspectionApiary && (
        <EnhancedInspectionForm 
          selectedHive={inspectionHive}
          selectedApiary={inspectionApiary}
          onClose={handleCloseInspectionForm}
          onSave={handleInspectionSaved}
        />
      )}
    </div>
  );
};

export default HivesPageEnhanced;